import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const POPULAR = ["gold coin", "silver ring", "mangalsutra", "jhumka", "25 mg coin", "name pendant", "anklet", "bangle"];
const DEFAULT_TERMS = ["silver coin", "Ganesh coin", "name pendant", "mangalsutra", "1 gm gold coin", "wedding gift", "jhumka", "belpatra"];

export default function SearchDrawer() {
  const { searchOpen, setSearchOpen, config } = useStore();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);
  const [termIdx, setTermIdx] = useState(0);
  const navigate = useNavigate();
  const timer = useRef(null);

  // Rotating placeholder (admin-editable via settings.search_placeholders),
  // pauses on focus, respects reduced-motion.
  const terms = config?.search_placeholders?.length ? config.search_placeholders : DEFAULT_TERMS;
  useEffect(() => {
    if (focused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setTermIdx((i) => (i + 1) % terms.length), 2600);
    return () => clearInterval(t);
  }, [focused, terms.length]);

  useEffect(() => {
    if (!searchOpen) { setQ(""); setResults(null); }
  }, [searchOpen]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    setBusy(true);
    timer.current = setTimeout(() => {
      api.get("/search", { params: { q: q.trim(), limit: 8 } })
        .then((r) => setResults(r.data))
        .catch(() => setResults({ items: [], total: 0 }))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (path) => { setSearchOpen(false); navigate(path); };

  return (
    <Sheet open={searchOpen} onOpenChange={setSearchOpen}>
      <SheetContent className="w-full sm:max-w-md bg-ivory p-0 flex flex-col" data-testid="search-drawer">
        <SheetHeader className="px-5 py-4 border-b border-brand/10 bg-white">
          <SheetTitle className="font-heading">Search Siyaara</SheetTitle>
        </SheetHeader>
        <div className="p-5">
          <form
            onSubmit={(e) => { e.preventDefault(); if (q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`); }}
            className="relative"
          >
            <Search size={16} className="absolute left-3 top-3 text-ink-2" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={`Search "${terms[termIdx % terms.length]}"`}
              className="h-10 w-full rounded-full border border-brand/20 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
              data-testid="search-drawer-input"
            />
          </form>

          {busy && <p className="text-xs font-mono text-ink-2 mt-4">SEARCHING…</p>}

          {results && !busy && (
            <div className="mt-4" data-testid="search-results">
              {results.corrected && (
                <p className="text-xs text-ink-2 mb-2" data-testid="search-corrected">
                  Showing results for <span className="font-medium text-brand">{results.corrected}</span>
                </p>
              )}
              {results.items.length === 0 ? (
                <p className="text-sm text-ink-2 py-6 text-center" data-testid="search-empty">No matches — try a metal, weight or design.</p>
              ) : (
                <ul className="divide-y divide-brand/10">
                  {results.items.map((p) => (
                    <li key={p.id}>
                      <button onClick={() => go(`/products/${p.slug}`)} className="w-full flex items-center gap-3 py-3 text-left group" data-testid={`search-result-${p.slug}`}>
                        <img src={(p.images || [])[0]} alt="" className="h-12 w-12 object-cover bg-white border border-brand/10 rounded-lg" />
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium truncate group-hover:text-brand transition-colors">{p.title}</span>
                          <span className="block text-[11px] font-mono text-ink-2">{p.purity} {p.metal}</span>
                        </span>
                        <span className="text-sm font-semibold gold-num">{inr(p.price_min)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {results.total > results.items.length && (
                <button onClick={() => go(`/search?q=${encodeURIComponent(q.trim())}`)} className="mt-3 text-sm text-brand underline underline-offset-4 decoration-gold" data-testid="search-view-all">
                  View all {results.total} results
                </button>
              )}
            </div>
          )}

          {!results && (
            <div className="mt-5">
              <p className="eyebrow mb-3">POPULAR SEARCHES</p>
              <div className="flex flex-wrap gap-2" data-testid="search-popular">
                {POPULAR.map((t) => (
                  <button key={t} onClick={() => go(`/search?q=${encodeURIComponent(t)}`)}
                    className="text-xs border border-brand/20 rounded-full px-3 py-1.5 hover:border-brand hover:text-brand transition-colors"
                    data-testid={`search-popular-${t.replace(/\s+/g, "-")}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
